import commaNumber from 'comma-number';
import { useCallback } from 'react';

import { MainText } from '@/components/Typography/NamedTypo';
import {
  COMPANY_UNKNOWN,
  CUSTOMER_ANONYMOUS,
  NOT_ASSIGNED_LABEL,
} from '@/configs/reserved-word';
import CategoryOnRow from '@/features/Commons/CustomCellItem/CategoryOnRow';
import TagSelectorOnRow from '@/features/Commons/CustomCellItem/TagSelectorOnRow';
import DateRowChildren from '@/features/Commons/DataTableCustomComponents/DateRowChildren';
import IconTextProperty from '@/features/Commons/Label/IconTextProperty';
import TicketLabel from '@/features/Commons/Ticket/TicketLabel';
import AgentItem from '@/features/Feedback/components/ContentItem/AgentItem';
import CompanyRowItem from '@/features/Feedback/components/ContentItem/CompanyRowItem';
import CustomerRowItem from '@/features/Feedback/components/ContentItem/CustomerRowItem';
import FeedbackTitleDisplay from '@/features/Feedback/components/ContentItem/FeedbackTitleDisplay';
import PriorityDropdown from '@/features/Feedback/field/Priority/PriorityDropdown';
import SentimentDropdown from '@/features/Feedback/field/Sentiment/SentimentDropdown';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import { priorityMap } from '@/types/feedbackConst';
import {
  ENGAGEMENT_COLUMNS,
  FEEDBACK_COLUMNS,
  SCORE_COLUMNS,
  SENTIMENT_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import feedbackUtils from '@/utils/feedback';

import SourcePropertyValue from '../FeedbackDetail/ChannelSourcePropertyValue';

type FeedbackRowItemMap = {
  [key: string]: DataTableRowItemT;
};

const useGetDefaultFeedbackRowItemMap = (
  setSelectedFeedback: (alias: string | null) => void,
) => {
  const { feedback_view } = useColumnConfigStore(
    (state) => state.columnConfigs,
  );
  const columnWidth = feedback_view.find((c) => c.name === 'tag')?.width;
  const { changeSentiment, changeEndingSentiment, changePriority } =
    useFeedbackListStore();

  const getDefaultRowItemMap = useCallback(
    (feedback: FeedbackT, rowKey: string): FeedbackRowItemMap => {
      const tagFilterList: string[] = [...new Set(feedback.labels || [])];

      const PriorityIcon =
        priorityMap[(feedback.priority) || 1].icon;
      const priorityText =
        priorityMap[(feedback.priority) || 1].value;

      const rowItemMap = {
        [FEEDBACK_COLUMNS.TICKET]: {
          columnKey: FEEDBACK_COLUMNS.TICKET,
          children: <TicketLabel type="feedback">{feedback.tid}</TicketLabel>,
          rawData: feedback.tid.split('-')[1],
        },

        [SOURCE_COLUMNS.SOURCE_TYPE]: {
          columnKey: SOURCE_COLUMNS.SOURCE_TYPE,
          children: <SourcePropertyValue {...feedback.source} />,
          rawData: feedback.source.type,
        },
        [FEEDBACK_COLUMNS.COMPNAY]: {
          columnKey: FEEDBACK_COLUMNS.COMPNAY,
          children: (
            <CompanyRowItem
              alias={feedback.company}
              name={feedback.company_info?.name}
            />
          ),
          rawData: feedback.company || COMPANY_UNKNOWN,
        },
        [FEEDBACK_COLUMNS.CUSTOMER]: {
          columnKey: FEEDBACK_COLUMNS.CUSTOMER,
          children: (
            <CustomerRowItem
              alias={feedback.customer}
              name={feedback.customer_info?.name}
            />
          ),
          rawData: feedback.customer || CUSTOMER_ANONYMOUS,
        },
        [FEEDBACK_COLUMNS.TITLE]: {
          columnKey: FEEDBACK_COLUMNS.TITLE,
          children: <FeedbackTitleDisplay {...feedback} />,
          onCellClick: () => setSelectedFeedback(feedback.alias),
          rawData: feedback.title || '',
        },
        [FEEDBACK_COLUMNS.CATEGORY]: {
          columnKey: FEEDBACK_COLUMNS.CATEGORY,
          children: <CategoryOnRow classification={feedback.classification} />,
          rawData: feedback.classification,
        },
        [FEEDBACK_COLUMNS.TAG]: {
          columnKey: FEEDBACK_COLUMNS.TAG,
          children: (
            <TagSelectorOnRow
              tagList={tagFilterList}
              listStyle={{
                maxWidth: 'auto',
                overflow: 'hidden',
                flexWrap: 'nowrap',
              }}
              onClick={() => setSelectedFeedback(feedback.alias)}
              columnWidth={columnWidth}
            />
          ),
          rawData: tagFilterList,
        },
        [FEEDBACK_COLUMNS.PRIORITY]: {
          columnKey: FEEDBACK_COLUMNS.PRIORITY,
          children: (
            <PriorityDropdown
              name={`feedback-list-${rowKey}-priority-dropdown`}
              selected={feedback.priority || 1}
              selectedNode={
                <IconTextProperty icon={PriorityIcon} value={priorityText} />
              }
              onSelect={(priorityKey) =>
                changePriority(feedback.alias, priorityKey)
              }
            />
          ),
          rawData: feedback.priority || 1,
        },
        [FEEDBACK_COLUMNS.DATE]: {
          columnKey: FEEDBACK_COLUMNS.DATE,
          children: <DateRowChildren date={feedback?.date || ''} />,
          rawData: feedback?.date || '-',
        },
        [FEEDBACK_COLUMNS.AGENT]: {
          columnKey: FEEDBACK_COLUMNS.AGENT,
          children: <AgentItem alias={feedback?.manager || ''} />,
          rawData: feedback?.manager || NOT_ASSIGNED_LABEL,
        },
        [SENTIMENT_COLUMNS.KEY_SENTIMENT]: {
          columnKey: SENTIMENT_COLUMNS.KEY_SENTIMENT,
          children: (
            <SentimentDropdown
              name={`feedback-list-${feedback.alias}-sentiment-dropdown`}
              feedbackKey={feedback.alias}
              selected={feedback.sentiment || 2}
              onSelect={(key) => changeSentiment(feedback.alias, Number(key))}
            />
          ),
          rawData: feedback.sentiment || 2,
        },
        [SENTIMENT_COLUMNS.ENDING_SENTIMENT]: {
          columnKey: SENTIMENT_COLUMNS.ENDING_SENTIMENT,
          children: (
            <SentimentDropdown
              name={`feedback-list-${feedback.alias}-ending-sentiment-dropdown`}
              feedbackKey={feedback.alias}
              selected={feedback.ending_sentiment}
              onSelect={(key) =>
                changeEndingSentiment(feedback.alias, Number(key))
              }
            />
          ),
          rawData: feedback.ending_sentiment,
        },
        [SOURCE_COLUMNS.DATA_ORIGIN]: {
          columnKey: SOURCE_COLUMNS.DATA_ORIGIN,
          children: <MainText>{feedback.data_origin}</MainText>,
          rawData: feedback.data_origin,
        },
        [FEEDBACK_COLUMNS.REMARKS]: {
          columnKey: FEEDBACK_COLUMNS.REMARKS,
          children: (
            <MainText ellipsis>{feedback.remarks?.join(', ')}</MainText>
          ),
          rawData: feedback.remarks,
        },
        [SCORE_COLUMNS.RATING_SCORE]: {
          columnKey: SCORE_COLUMNS.RATING_SCORE,
          children: <MainText>{feedback.rating_score}</MainText>,
          rawData: feedback.rating_score,
        },
        [SCORE_COLUMNS.NPS_SCORE]: {
          columnKey: SCORE_COLUMNS.NPS_SCORE,
          children: <MainText>{feedback.nps_score}</MainText>,
          rawData: feedback.nps_score,
        },
        [SCORE_COLUMNS.CES_SCORE]: {
          columnKey: SCORE_COLUMNS.CES_SCORE,
          children: <MainText>{feedback.ces_score}</MainText>,
          rawData: feedback.ces_score,
        },
        [SCORE_COLUMNS.CSAT_SCORE]: {
          columnKey: SCORE_COLUMNS.CSAT_SCORE,
          children: <MainText>{feedback.csat_score}</MainText>,
          rawData: feedback.csat_score,
        },
        [FEEDBACK_COLUMNS.TICKET_STATUS]: {
          columnKey: FEEDBACK_COLUMNS.TICKET_STATUS,
          children: (
            <IconTextProperty
              {...feedbackUtils.getSourceStatusLabel(feedback.source_status)}
            />
          ),
          rawData: feedback.source_status,
        },
        [FEEDBACK_COLUMNS.STATUS]: {
          columnKey: FEEDBACK_COLUMNS.STATUS,
          children: feedbackUtils.getFeedbackStatusLabel(feedback.status),
          rawData: feedback.status,
        },
        [SOURCE_COLUMNS.SOURCE_TARGET]: {
          columnKey: SOURCE_COLUMNS.SOURCE_TARGET,
          children: (
            <MainText dim={!feedback.source.target}>
              {feedback.source.target || NOT_ASSIGNED_LABEL}
            </MainText>
          ),
          rawData: feedback.source.target,
        },
        [FEEDBACK_COLUMNS.IMPRESSION]: {
          columnKey: FEEDBACK_COLUMNS.IMPRESSION,
          children: (
            <MainText dim={!feedback.impression}>
              {feedback.impression ? commaNumber(feedback.impression) : '-'}
            </MainText>
          ),
          rawData: feedback.impression,
        },
        [ENGAGEMENT_COLUMNS.ENGAGEMENT_RATE]: {
          columnKey: ENGAGEMENT_COLUMNS.ENGAGEMENT_RATE,
          children: (
            <MainText dim={!feedback.engagement?.rate}>
              {feedback.engagement?.rate
                ? commaNumber(feedback.engagement?.rate.toFixed(1))
                : '-'}
            </MainText>
          ),
          rawData: feedback.engagement?.rate,
        },
        [ENGAGEMENT_COLUMNS.TOTAL_ENGAGEMENT]: {
          columnKey: ENGAGEMENT_COLUMNS.TOTAL_ENGAGEMENT,
          children: (
            <MainText dim={!feedback.engagement?.total}>
              {feedback.engagement?.total
                ? commaNumber(feedback.engagement?.total)
                : '-'}
            </MainText>
          ),
          rawData: feedback.engagement?.total,
        },
      };

      return rowItemMap;
    },
    [
      columnWidth,
      setSelectedFeedback,
      changePriority,
      changeSentiment,
      changeEndingSentiment,
    ],
  );

  return getDefaultRowItemMap;
};

export default useGetDefaultFeedbackRowItemMap;
